import { Component } from 'preact';
import {
  GOLD, GOLD_TEXT, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, BG_ATMOSPHERE, VIGNETTE, KnotOrnament,
} from '../lib/medievalTheme';
import { EM_DASH } from '../lib/typography';
import { CloseButton } from './ui/navButtons';
import { fanProjectDisclaimerParagraphs } from './fanProjectDisclaimer';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { k, t } from '../localization/t';

// Credits are tiered by relationship, not by discipline. The working team
// is one flat roster: each member is named with their role inline
// ("Name, Role") rather than filed under a discipline heading, so a team
// of one per post reads as a list of people, not a stack of one-name
// sections. Everyone who contributed once, or from outside, is
// acknowledged by contribution in the next tier instead.
// Sorted by name so no individual outranks a peer.
const TEAM = [
  { name: 'Simon Savin',      role: k('UI/UX Designer') },
  { name: 'Niklas Jacobsen',  role: k('Software Developer') },
  { name: 'Libra',            role: k('Community Management'),  discord: 'l1.bra' },
  { name: 'Chris Roberts',    role: k('QA Lead') },
];

const CONTRIBUTORS = [
  { name: 'Collector Arthouse', note: k('Partnership'),   url: 'https://www.collectorarthouse.com/category/all-products' },
  { name: 'Séverine Pineaux',   note: k('Illustrations'), url: 'https://www.pineaux.com/' },
  { name: 'Positronix',         note: k('Artwork'),       discord: 'positronix' },
];

// Original closed-beta testers. Every account that existed when the
// closed beta opened (i.e. is_founder = TRUE in the DB at the time of
// authoring). Frozen list: founders are locked at beta cutoff and the
// credits screen reads as authored credits, not a live roster.
// Add new contributors manually here if anyone earns a credit later.
const BETA_TESTERS = [
  { name: 'ABlueCuddleBear' },
  { name: 'Abnar' },
  { name: 'Acme' },
  { name: 'AgentChadd' },
  { name: 'Alcappachino' },
  { name: 'BRICK' },
  { name: 'Bwatos',           discord: 'bwatos' },
  { name: 'Clutterfox',       discord: 'clutterfox' },
  { name: 'Corrinmana' },
  { name: 'DatGuyHammer' },
  { name: 'Dereon' },
  { name: 'Detective Steel.' },
  { name: 'Feather' },
  { name: 'Felixo11' },
  { name: 'ForceOfWill',      discord: 'mista_girlboss' },
  { name: 'Heuth' },
  { name: 'Jollop' },
  { name: 'Jowiii' },
  { name: 'Karnifact' },
  { name: 'Kitty' },
  { name: 'L1BRA',            discord: 'l1.bra' },
  { name: 'lobster' },
  { name: 'LucifersKid' },
  { name: 'M4nd0' },
  { name: 'Milldog' },
  { name: 'Moustache' },
  { name: 'Qrupt' },
  { name: 'Rhystic_Buddy' },
  { name: 'ryutras' },
  { name: 'Saboxa' },
  { name: 'Scottie' },
  { name: 'Seugruom' },
  { name: 'SiF',              discord: 'sifsquared' },
  { name: 'so0wa' },
  { name: 'SpookieSpace' },
  { name: 'Squire Lep',       discord: 'leprechaun003' },
  { name: 'System' },
  { name: 'The Chog' },
  { name: 'The Majuscule' },
  { name: 'TheGrandSinnovia' },
  { name: 'Trashmang' },
  { name: 'Triumph' },
  { name: 'Unagi' },
  { name: 'Wiedzmin',         discord: 'wiedzmin.' },
  { name: 'Windy' },
  { name: 'wolle' },
  { name: 'Wormy' },
  { name: 'xannysosa' },
  { name: 'XANZABAR',         discord: 'xanzabar_' },
  { name: 'Yoshi' },
  { name: 'ZackysBack' },
];

const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

// Tier heading. Same ornament recipe as the screen title, one step down,
// so the role headings below it read as subordinate rather than as peers.
// The flanking rules are flex-1 with a cap rather than a fixed width, so
// the heading survives the narrower column it sits in when the team and
// thanks tiers run side by side.
function TierHeading({ children }) {
  return (
    <div className="flex items-center justify-center gap-4 mb-10">
      <KnotOrnament side="left" size={22} />
      <div className="h-px flex-1 max-w-16" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.45) 100%)` }} />
      <h2
        className="arena-heading whitespace-nowrap"
        style={{
          color: TEXT_PRIMARY,
          fontSize: '1.5cqw',
          textShadow: '0 2px 6px rgba(0,0,0,0.75), 0 0 18px rgba(212,168,67,0.22)',
        }}
      >
        {children}
      </h2>
      <div className="h-px flex-1 max-w-16" style={{ background: `linear-gradient(90deg, ${GOLD} 0.45) 0%, transparent 100%)` }} />
      <KnotOrnament side="right" size={22} />
    </div>
  );
}

function CategoryTagline({ children }) {
  return (
    <p
      className="text-center italic mb-6 -mt-3"
      style={{ color: TEXT_MUTED, fontSize: '0.75cqw', lineHeight: 1.6 }}
    >
      {children}
    </p>
  );
}

// Team member line. Role and discord handle ride on the same line as the
// name ("Name, Role @handle") so the roster reads as a list of people
// rather than a stack of discipline headings. Both sit below the name in
// size so the person still out-weighs the post they hold.
function MemberLine({ name, role, discord, url }) {
  const nameStyle = {
    color: url ? `${GOLD_TEXT} 1)` : TEXT_PRIMARY,
    fontSize: '1.05cqw',
    textShadow: '0 1px 4px rgba(0,0,0,0.55)',
    lineHeight: 1.2,
  };
  return (
    <div className="mb-3 flex flex-wrap items-baseline justify-center gap-x-1.5">
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="arena-heading"
          style={nameStyle}
        >
          {name}
        </a>
      ) : (
        <span className="arena-heading" style={nameStyle}>
          {name}
        </span>
      )}
      {role ? (
        <span style={{ color: TEXT_BODY, fontSize: '0.8cqw' }}>, {t(role)}</span>
      ) : null}
      {discord ? (
        <span style={{ color: TEXT_MUTED, fontFamily: 'monospace', fontSize: '0.7cqw' }}>
          @{discord}
        </span>
      ) : null}
    </div>
  );
}

// Contributor line. Name stays at team size. The differentiation is
// structural (its own tier, no post, contribution named inline), not a
// demotion in type size.
function ContributorLine({ name, note, discord, url }) {
  const nameStyle = {
    color: url ? `${GOLD_TEXT} 1)` : TEXT_PRIMARY,
    fontSize: '1.05cqw',
    textShadow: '0 1px 4px rgba(0,0,0,0.55)',
    lineHeight: 1.2,
  };
  return (
    <div className="mb-4 flex flex-wrap items-baseline justify-center gap-x-2">
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="arena-heading" style={nameStyle}>
          {name}
        </a>
      ) : (
        <span className="arena-heading" style={nameStyle}>{name}</span>
      )}
      {note ? (
        <span style={{ color: TEXT_BODY, fontSize: '0.8cqw' }}>
          {EM_DASH} {t(note)}
        </span>
      ) : null}
      {discord ? (
        <span style={{ color: TEXT_MUTED, fontFamily: 'monospace', fontSize: '0.7cqw' }}>
          @{discord}
        </span>
      ) : null}
    </div>
  );
}

// Beta tester cell. The roster is ~50 names; one per line gave the frozen
// list several times the vertical space of the working team. A grid reads
// as a wall of names rather than a list of posts.
function TesterName({ name, discord }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span
        className="arena-heading"
        style={{
          color: TEXT_PRIMARY,
          fontSize: '0.9cqw',
          textShadow: '0 1px 4px rgba(0,0,0,0.55)',
          lineHeight: 1.2,
        }}
      >
        {name}
      </span>
      {discord ? (
        <span style={{ color: TEXT_MUTED, fontFamily: 'monospace', fontSize: '0.6cqw' }}>
          @{discord}
        </span>
      ) : null}
    </div>
  );
}

export default class CreditsScreen extends Component {
  componentDidMount() {
    this._escDismissId = pushDismiss(() => this.props.onClose?.());
  }

  componentWillUnmount() {
    removeDismiss(this._escDismissId);
  }

  render() {
    const { onClose } = this.props;
    return (
      <div
        className="fixed inset-0 z-[120] flex flex-col"
        style={{ background: `url("/tex-noise.webp"), ${BG_ATMOSPHERE}` }}
      >
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative flex-1 flex flex-col min-h-0">

          {/* Header. Same recipe as HallOfFameScreen so the credits
              screen visually nests in the same family as the other
              full-screen game surfaces. */}
          <div className="relative shrink-0 flex items-center justify-center pt-10 pb-4 px-12">
            <div className="flex items-center gap-8" aria-hidden="true">
              <KnotOrnament side="left" size={32} />
              <div className="h-px w-24" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.5) 100%)` }} />
              <h1
                className="arena-heading font-bold"
                style={{
                  color: TEXT_PRIMARY,
                  fontSize: '2.25cqw',
                  textShadow: '0 3px 8px rgba(0,0,0,0.8), 0 0 22px rgba(212,168,67,0.3)',
                }}
              >
                {t('Credits')}
              </h1>
              <div className="h-px w-24" style={{ background: `linear-gradient(90deg, ${GOLD} 0.5) 0%, transparent 100%)` }} />
              <KnotOrnament side="right" size={32} />
            </div>
            <div className="absolute right-12 top-10">
              <CloseButton title={t('Close')} onClick={onClose} />
            </div>
          </div>

          {/* Body. Centered, scrolls vertically */}
          <div className="flex-1 min-h-0 overflow-y-auto px-12 pb-16">
            <div className="mx-auto w-full max-w-[56cqw] pt-6">

              {/* Disclaimer. Shared with the intro splash via
                  fanProjectDisclaimer.jsx so the wording stays in
                  lockstep across both surfaces. */}
              <div className="mx-auto" style={{ maxWidth: '32cqw' }}>
                {fanProjectDisclaimerParagraphs().map((content, i) => (
                  <p
                    key={i}
                    className="text-center italic"
                    style={{
                      color: TEXT_MUTED,
                      fontSize: '0.75cqw',
                      lineHeight: 1.75,
                      marginTop: i === 0 ? 0 : '0.75cqw',
                    }}
                  >
                    {content}
                  </p>
                ))}
              </div>

              {/* Symmetric gold separator. Fades on both sides so it
                  reads as a centered ornament. */}
              <div
                className="mx-auto mt-10 h-px"
                style={{
                  width: '60%',
                  background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.45) 50%, transparent 100%)`,
                }}
              />

              {/* Top-billed authorship credit. Sits above the tiers on
                  its own: "Created by" claims everything not otherwise
                  credited, so it belongs to no section. The name is
                  sized between the role members and the screen title. */}
              <section className="mt-14 flex flex-col items-center gap-1">
                <span
                  className="arena-heading"
                  style={{
                    color: `${GOLD} 0.8)`,
                    fontSize: '0.95cqw',
                    textShadow: `0 0 14px ${GOLD} 0.18)`,
                  }}
                >
                  {t('Created by')}
                </span>
                <span
                  className="arena-heading"
                  style={{
                    color: TEXT_PRIMARY,
                    fontSize: '1.6cqw',
                    textShadow: '0 2px 6px rgba(0,0,0,0.75), 0 0 18px rgba(212,168,67,0.22)',
                  }}
                >
                  Fabian Urbanek
                </span>
                <span style={{ color: TEXT_MUTED, fontFamily: 'monospace', fontSize: '0.7cqw' }}>
                  @clutterfox
                </span>
              </section>

              {/* Three tiers, descending involvement: the working team,
                  then everyone credited for a contribution, then the
                  frozen beta roster. Each tier gets a different block
                  shape. Shape separates them more legibly than colour or
                  size, and it needs no new ornament vocabulary.
                  The first two tiers run side by side rather than
                  stacked: stacking is what buries a partnership half a
                  screen below the fold. Side by side, the thanks tier
                  starts at the same eye line as the team while its own
                  column keeps the two kinds of credit distinct. */}
              <div className="mt-14 grid grid-cols-[1.25fr_1px_1fr] gap-x-10 items-start">
                <section>
                  <TierHeading>{t('The Team')}</TierHeading>
                  <div>
                    {TEAM.slice().sort(byName).map((m) => (
                      <MemberLine key={m.discord || m.name} name={m.name} role={m.role} discord={m.discord} url={m.url} />
                    ))}
                  </div>
                </section>

                <div
                  className="self-stretch w-px"
                  style={{ background: `linear-gradient(180deg, transparent 0%, ${GOLD} 0.32) 50%, transparent 100%)` }}
                />

                <section>
                  <TierHeading>{t('With Thanks To')}</TierHeading>
                  {CONTRIBUTORS.slice().sort(byName).map((c) => (
                    <ContributorLine key={c.name} name={c.name} note={c.note} discord={c.discord} url={c.url} />
                  ))}
                </section>
              </div>

              <div>
                {/* Separator before the frozen roster. It's a different
                    kind of credit again, so keep the break explicit. */}
                <div
                  className="mx-auto mt-20 h-px"
                  style={{
                    width: '60%',
                    background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.45) 50%, transparent 100%)`,
                  }}
                />

                <section className="mt-16">
                  <TierHeading>{t('Original Beta Testers')}</TierHeading>
                  <CategoryTagline>
                    {t('Every closed-beta tester who walked the halls before the gates opened.')}
                  </CategoryTagline>
                  <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                    {BETA_TESTERS.slice().sort(byName).map((t) => (
                      <TesterName key={t.name} name={t.name} discord={t.discord} />
                    ))}
                  </div>
                </section>
              </div>

            </div>
          </div>

        </div>
      </div>
    );
  }
}
